import { Configuration, IPublicClientApplication, LogLevel } from "@azure/msal-browser";

declare const __WIDGET_BUILD__: boolean;

// Azure AD configuration from environment variables
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID || "";
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || "";
const azureAdEnabled = import.meta.env.VITE_AZURE_AD_ENABLED === "true";

// Widget detection — avoid auth when embedded
const detectWidgetMode = (): boolean => {
    try {
        const isWidgetBuild = typeof __WIDGET_BUILD__ !== "undefined" && __WIDGET_BUILD__;
        const hasWidgetScript = !!document.querySelector('script[src*="widget.js"]');
        const pathSuggestsWidget = window.location.pathname.includes("widget");
        const globalWidgetFlag =
            (window as any).PublicWidgetConfig?.widgetMode === true || (window as any).IS_CHAT_WIDGET === true;
        return isWidgetBuild || hasWidgetScript || pathSuggestsWidget || globalWidgetFlag;
    } catch {
        return false;
    }
};

const isWidgetMode = detectWidgetMode();

export const isAuthEnabled = azureAdEnabled && !isWidgetMode;

export const msalConfig: Configuration = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin
    },
    cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                }
            },
            logLevel: LogLevel.Warning
        }
    }
};

export const loginRequest = {
    scopes: ["User.Read", "Directory.Read.All"]
};

export const tokenRequest = {
    scopes: ["User.Read", "Directory.Read.All"]
};

// ---------------------------------------------------------------------------
// Backward-compatible exports used by Chat.tsx, Ask.tsx, AnalysisPanel, etc.
// These bridge the old auth API surface to the new MSAL-direct approach.
// ---------------------------------------------------------------------------

/** Legacy alias — true when auth is enabled (replaces old `useLogin`). */
export const useLogin = isAuthEnabled;

/** Legacy — no App Services token in the new approach. */
export const appServicesToken = null;

/** Legacy — access control is enforced when auth is enabled. */
export const requireAccessControl = false;

/** Legacy — not used in the new approach, kept for compatibility. */
export const isDevMode = !isAuthEnabled;

/** Get an access token for API calls. */
export const getToken = (client: IPublicClientApplication): Promise<string | undefined> => {
    return client
        .acquireTokenSilent({
            ...tokenRequest,
            redirectUri: window.location.origin
        })
        .then(r => r.accessToken)
        .catch(error => {
            console.error("Silent token acquisition failed:", error);
            return undefined;
        });
};

/** Check whether a user is currently signed in. */
export const isLoggedIn = (client: IPublicClientApplication | undefined): boolean => {
    return client?.getActiveAccount() != null;
};

/** Legacy redirect URI helper. */
export const getRedirectUri = () => window.location.origin;

/** Legacy logout — no-op (handled by LoginButton via MSAL). */
export const appServicesLogout = () => {
    window.location.href = "/";
};
